import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatCurrency = (value: number | null | undefined, currency: string = 'usd') => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  const symbols = { usd: '$', eur: '€', gbp: '£', jpy: '¥', mxn: '$', php: '₱' };
  const symbol = symbols[currency as keyof typeof symbols] || '$';

  if (value >= 1e9) return `${symbol}${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${symbol}${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${symbol}${(value / 1e3).toFixed(2)}K`;

  return `${symbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatPrice = (price: number | null | undefined, currency: string = 'usd') => {
  if (price === null || price === undefined || isNaN(price)) {
    return '-';
  }
  
  const symbols = { usd: '$', eur: '€', gbp: '£', jpy: '¥', mxn: '$', php: '₱' };
  const symbol = symbols[currency as keyof typeof symbols] || '$';

  if (price < 0.01) {
    return `${symbol}${price.toFixed(6)}`;
  }
  if (price < 1) {
    return `${symbol}${price.toFixed(4)}`;
  }
  return `${symbol}${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatPercentage = (value: number | null | undefined) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

export const formatNumber = (value: number | null | undefined) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }
  
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toLocaleString();
};

export const formatDate = (timestamp: number) => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatDateTime = (timestamp: number) => {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getChangeClass = (change: number | null | undefined) => {
  if (change === null || change === undefined || isNaN(change)) {
    return 'text-gray-600 dark:text-gray-400';
  }
  if (change > 0) return 'text-green-600 dark:text-green-400';
  if (change < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-600 dark:text-gray-400';
};

export const getChangeBgClass = (change: number | null | undefined) => {
  if (change === null || change === undefined || isNaN(change)) {
    return 'bg-gray-100 dark:bg-gray-800';
  }
  if (change > 0) return 'bg-green-100 dark:bg-green-900/30';
  if (change < 0) return 'bg-red-100 dark:bg-red-900/30';
  return 'bg-gray-100 dark:bg-gray-800';
};